import { create } from 'zustand';

import { TimetableRepository } from '../../api/repository/timetable';
import type { Id, QltTimetableDayCell, QltTimetableWeek, QltTimetableWeekCell } from '../../api/types';
import { userError } from '../userError';
import { type MaybeMsg } from '../../i18n/msg.ts';

export interface QltTimetableState {
  todayCells: QltTimetableDayCell[];
  week: QltTimetableWeek | null;
  cells: Record<string, QltTimetableWeekCell>;
  holidays: string[];
  loading: boolean;
  error: MaybeMsg | null;
  loadToday: (teacherId: Id, date: string) => Promise<QltTimetableDayCell[]>;
  loadWeek: (teacherId: Id, weekOf: string) => Promise<QltTimetableWeek | null>;
  reset: () => void;
}

export const useQltTimetableStore = create<QltTimetableState>((set) => ({
  todayCells: [],
  week: null,
  cells: {},
  holidays: [],
  loading: false,
  error: null,

  async loadToday(teacherId, date) {
    set({ loading: true, error: null });
    try {
      const todayCells = await TimetableRepository.fetchDay({
        scope: 'teacher',
        id: teacherId,
        date,
        mode: 'actual',
      });
      set({ todayCells, loading: false });
      return todayCells;
    } catch (error) {
      set({ loading: false, error: userError(error) });
      return [];
    }
  },

  async loadWeek(teacherId, weekOf) {
    set({ loading: true, error: null });
    try {
      const week = await TimetableRepository.fetchWeek({
        scope: 'teacher',
        id: teacherId,
        weekOf,
        mode: 'actual',
      });
      set({
        week,
        cells: week.cells || {},
        holidays: week.holidays || [],
        loading: false,
      });
      return week;
    } catch (error) {
      set({ loading: false, error: userError(error) });
      return null;
    }
  },

  reset: () => set({ todayCells: [], week: null, cells: {}, holidays: [], error: null }),
}));
